// Lesbare Namen für die Schlüssel der Recherche.
//
// In POC v1 kam diese Liste vom Server. Hier steht sie in der Oberfläche, weil
// sie NUR dort gebraucht wird: Die Werte selbst sind als CHECK in der Datenbank
// festgelegt und ändern sich mit dem Schema — ein zweiter Endpunkt, der sie
// nur übersetzt, wäre eine Schicht ohne Aufgabe.
//
// Fehlt ein Schlüssel, steht der Rohwert da. Sichtbar falsch ist besser als
// unsichtbar fehlend: Ein leeres Feld sähe aus wie „nicht gesetzt".

const CASE_TYPE: Record<string, string> = {
  LITIGATION: 'Zivilverfahren',
  CLASS_ACTION: 'Sammelklage',
  PRODUCT_LIABILITY: 'Produkthaftung',
  REGULATORY_INVESTIGATION: 'Aufsichtsverfahren',
  ANTITRUST: 'Kartellverfahren',
  GOVERNMENT_INVESTIGATION: 'Behördliche Untersuchung',
  TAX: 'Steuerverfahren',
  ENVIRONMENTAL: 'Umweltverfahren',
  SANCTIONS: 'Sanktionen',
  LICENSING_APPROVAL: 'Lizenzgenehmigung',
  PRODUCT_APPROVAL: 'Produktzulassung',
  SAFETY_SIGNAL: 'Sicherheitssignal',
  ACCOUNTING_AUDIT: 'Bilanzprüfung',
  CYBER_SECURITY: 'IT-Sicherheit',
  DATA_PROTECTION: 'Datenschutz',
  LABOR: 'Arbeitsrecht',
  M_AND_A: 'Fusionskontrolle',
  TAKEOVER: 'Übernahme',
  RESTRUCTURING: 'Restrukturierung',
  REFINANCING: 'Refinanzierung',
  COVENANT: 'Kreditauflage',
  CREDIT_RATING: 'Ratingverfahren',
  GOVERNMENT_CONTRACT: 'Staatsauftrag',
  POLITICAL_REGULATORY: 'Politik und Regulierung',
  COMPETITIVE_THREAT: 'Wettbewerbsbedrohung',
  SECTOR_EVENT: 'Branchenereignis',
  MANAGEMENT: 'Führungswechsel',
  OWNERSHIP: 'Eigentümerwechsel',
  // Die drei heißen wie Ereignisarten und meinen etwas anderes: Das Ereignis
  // ist der TERMIN, der Fall der VORGANG darum herum — mit Verlauf, Belegen
  // und einem Ausgang. Ein Termin lernt nichts; ein Vorgang schon.
  AGM: 'Hauptversammlung',
  DIVIDEND_POLICY: 'Dividendenpolitik',
  EARNINGS: 'Quartalszahlen',
  OTHER: 'Sonstiges',
}

const CASE_STATUS: Record<string, string> = {
  MONITORING: 'beobachtet',
  ACTIVE: 'laufend',
  PENDING_DECISION: 'Entscheidung offen',
  APPEAL: 'Berufung',
  SETTLEMENT: 'Vergleich',
  RESOLVED_POSITIVE: 'günstig beendet',
  RESOLVED_NEGATIVE: 'ungünstig beendet',
  RESOLVED_NEUTRAL: 'neutral beendet',
  CLOSED: 'abgeschlossen',
  UNKNOWN: 'unbekannt',
}

const CASE_STAGE: Record<string, string> = {
  ANNOUNCED: 'angekündigt',
  INVESTIGATION_OPENED: 'Untersuchung eröffnet',
  FILING: 'Klageeinreichung',
  DISCOVERY: 'Beweisaufnahme',
  HEARING: 'Anhörung',
  PRELIMINARY_DECISION: 'vorläufige Entscheidung',
  FINAL_DECISION: 'Endentscheidung',
  APPEAL_FILED: 'Berufung eingelegt',
  APPEAL_HEARING: 'Berufungsverhandlung',
  SETTLEMENT_NEGOTIATIONS: 'Vergleichsverhandlungen',
  SETTLEMENT_AGREED: 'Vergleich geschlossen',
  REMEDIATION: 'Umsetzung der Auflagen',
  CLOSED: 'geschlossen',
}

const UPDATE_TYPE: Record<string, string> = {
  NEW_CASE: 'Case angelegt',
  NEW_ALLEGATION: 'neuer Vorwurf',
  AUTHORITY_ACTION: 'Behördenschritt',
  COURT_DECISION: 'Gerichtsentscheidung',
  HEARING_SCHEDULED: 'Termin angesetzt',
  HEARING_COMPLETED: 'Anhörung beendet',
  APPEAL: 'Berufung',
  SETTLEMENT: 'Vergleich',
  SETTLEMENT_TALKS: 'Vergleichsgespräche',
  FINE_PROPOSED: 'Bußgeld angekündigt',
  FINE_CONFIRMED: 'Bußgeld festgesetzt',
  GUIDANCE_CHANGED: 'Prognose geändert',
  PROVISION_CHANGED: 'Rückstellung geändert',
  DEADLINE_CHANGED: 'Frist verschoben',
  CASE_DELAYED: 'Verfahren verzögert',
  CASE_DISMISSED: 'Verfahren eingestellt',
  FINAL_RESOLUTION: 'Abschluss',
  ASSESSMENT_CHANGED: 'Einschätzung geändert',
  OTHER: 'Sonstiges',
}

const SOURCE_TYPE: Record<string, string> = {
  COMPANY_IR: 'Investor Relations',
  AD_HOC: 'Ad-hoc-Meldung',
  ANNUAL_REPORT: 'Geschäftsbericht',
  QUARTERLY_REPORT: 'Quartalsbericht',
  COURT: 'Gericht',
  REGULATOR: 'Aufsicht',
  GOVERNMENT: 'Behörde',
  EXCHANGE: 'Börse',
  SEC_FILING: 'SEC-Einreichung',
  BAFIN: 'BaFin',
  EU_COMMISSION: 'EU-Kommission',
  PRESS_AGENCY: 'Nachrichtenagentur',
  NEWSPAPER: 'Presse',
  ANALYST_REPORT: 'Analystenbericht',
  COMPANY_WEBSITE: 'Unternehmensseite',
  OTHER: 'Sonstiges',
}

const CATALYST_TYPE: Record<string, string> = {
  EARNINGS: 'Quartalszahlen',
  GUIDANCE: 'Prognose',
  CAPITAL_MARKETS_DAY: 'Kapitalmarkttag',
  AGM: 'Hauptversammlung',
  COURT_DECISION: 'Gerichtsentscheidung',
  HEARING: 'Anhörung',
  REGULATORY_DECISION: 'Aufsichtsentscheidung',
  PRODUCT_APPROVAL: 'Produktzulassung',
  ANTITRUST_DECISION: 'Kartellentscheidung',
  GOVERNMENT_DECISION: 'Behördenentscheidung',
  TENDER_DECISION: 'Vergabeentscheidung',
  DIVIDEND: 'Dividende',
  INDEX_CHANGE: 'Indexänderung',
  LOCKUP_EXPIRY: 'Ende der Haltefrist',
  DEBT_MATURITY: 'Fälligkeit',
  RATING_REVIEW: 'Ratingüberprüfung',
  OTHER: 'Sonstiges',
}

const ACTION_TYPE: Record<string, string> = {
  REVIEW_CASE: 'Case durchsehen',
  CHECK_AUTHORITY: 'Behörde prüfen',
  CHECK_COURT: 'Gericht prüfen',
  CHECK_COMPANY_IR: 'Investor Relations prüfen',
  CHECK_FILINGS: 'Einreichungen prüfen',
  CHECK_EARNINGS: 'Zahlen prüfen',
  UPDATE_FINANCIAL_EXPOSURE: 'Belastung aktualisieren',
  REASSESS_PROBABILITY: 'Wahrscheinlichkeit neu schätzen',
  REASSESS_TRADING_STATUS: 'Handelsstatus neu bewerten',
  ESCALATE: 'Eskalieren',
  OTHER: 'Sonstiges',
}

const INSTRUMENT_ROLE: Record<string, string> = {
  PRIMARY: 'hauptbetroffen',
  AFFECTED: 'mittelbar betroffen',
  ACQUIRER: 'Erwerber',
  TARGET: 'Zielgesellschaft',
  PEER: 'beobachtet',
}

const TRADING_IMPACT: Record<string, string> = {
  NORMAL: 'ohne Einschränkung',
  CAUTION: 'mit Vorsicht',
  REDUCED_SIZE: 'kleinere Position',
  REQUIRE_CONFIRMATION: 'Bestätigung nötig',
  NO_NEW_ENTRY: 'kein Neueinstieg',
  BLOCK: 'gesperrt',
}

const EXPOSURE_BASIS: Record<string, string> = {
  COMPANY_DISCLOSURE: 'Angabe des Unternehmens',
  COURT_FILING: 'Gerichtsakte',
  AUTHORITY_STATEMENT: 'Behördenangabe',
  ANALYST_ESTIMATE: 'Schätzung des Analysten',
  MEDIA_REPORT: 'Medienbericht',
  UNKNOWN: 'unbekannt',
}

const EXPECTATION_BASIS: Record<string, string> = {
  OFFICIAL_SOURCE: 'amtliche Quelle',
  COURT_CALENDAR: 'Terminkalender des Gerichts',
  COMPANY_STATEMENT: 'Angabe des Unternehmens',
  AUTHORITY_STATEMENT: 'Behördenangabe',
  MANAGEMENT_GUIDANCE: 'Prognose der Führung',
  HISTORICAL_PATTERN: 'Erfahrungswert',
  ANALYST_ESTIMATE: 'Schätzung des Analysten',
  MEDIA_REPORT: 'Medienbericht',
  UNKNOWN: 'unbekannt',
}

const OUTCOME: Record<string, string> = {
  BETTER: 'besser als erwartet',
  AS_EXPECTED: 'wie erwartet',
  WORSE: 'schlechter als erwartet',
}

const FREQUENCY: Record<string, string> = {
  DAILY: 'täglich',
  WEEKLY: 'wöchentlich',
  BIWEEKLY: 'zweiwöchentlich',
  MONTHLY: 'monatlich',
  QUARTERLY: 'quartalsweise',
}

const LISTS: Record<string, Record<string, string>> = {
  case_type: CASE_TYPE,
  case_status: CASE_STATUS,
  case_stage: CASE_STAGE,
  update_type: UPDATE_TYPE,
  source_type: SOURCE_TYPE,
  catalyst_type: CATALYST_TYPE,
  action_type: ACTION_TYPE,
  instrument_role: INSTRUMENT_ROLE,
  trading_impact: TRADING_IMPACT,
  exposure_basis: EXPOSURE_BASIS,
  expectation_basis: EXPECTATION_BASIS,
  outcome: OUTCOME,
  frequency: FREQUENCY,
}

/** Der lesbare Name eines Schlüssels. Unbekanntes bleibt stehen. */
export function label(list: keyof typeof LISTS,
                      value: string | null | undefined): string {
  if (!value) return '—'
  return LISTS[list]?.[value] ?? value
}

/** Die Auswahlliste für einen Filter: Schlüssel und Name. */
export function options(list: keyof typeof LISTS): [string, string][] {
  return Object.entries(LISTS[list] ?? {})
}

/**
 * Wie ein Zeitraum zu lesen ist — die Genauigkeit gehört zur Angabe.
 *
 * „Q4 2026" heißt Q4 2026 und nicht der 15. Oktober. Wer nur das Quartal
 * kennt, bekommt auch nur das Quartal zu sehen — ein ausgeschriebenes Datum
 * behauptete einen Termin, den es nicht gibt.
 */
export function period(from: string | null, to: string | null,
                       precision: string): string {
  if (!from && !to) return 'Zeitpunkt offen'
  const tag = (iso: string) => new Date(iso).toLocaleDateString('de-DE')

  switch (precision) {
    case 'EXACT':
      return `am ${tag(from!)}`
    case 'MONTH':
      return new Date(from!).toLocaleDateString('de-DE',
                                                { month: 'long', year: 'numeric' })
    case 'QUARTER': {
      const d = new Date(from!)
      return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`
    }
    case 'DATE_RANGE':
      return from && to ? `${tag(from)} – ${tag(to)}` : tag((from ?? to)!)
    case 'ESTIMATED':
      return `geschätzt ${tag((from ?? to)!)}`
    default:
      return 'Zeitpunkt offen'
  }
}
